import { AppDialog, AppSectionErrorBoundary, AppSpacer } from 'components/ui';
import { IOrganization, UserDataAndOrganization } from 'types/models/OrganizationModels';
import { useCallback, useMemo, useState } from 'react';

import { AppButton } from 'components/form';
import Check from '@mui/icons-material/Check';
import InviteUserStep from './components/InviteUserStep';
import SelectOrganizationStep from './components/SelectOrganizationStep';
import SelectUserStep from './components/SelectUserStep';
import { SingleUserType } from 'types/GlobalTypes';
import Step from '@mui/material/Step';
import { StepIconProps } from '@mui/material/StepIcon';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { UserData } from 'types/models/UserData';
import { UserType } from 'types/ProfileTypes';
import { observer } from 'mobx-react-lite';
import { useStores } from 'index';
import { useTranslation } from 'react-i18next';

type PropsType = {
  organizationData: IOrganization | null;
  isOpened: boolean;
  user: SingleUserType | null;
  onClose: () => void;
};

const allowedDirectAddDomains = ['@trackman.com', '@trackmanfootball.com'];

export const AddUserDialog = observer(({ organizationData, isOpened, onClose }: PropsType) => {
  if (!organizationData) return <></>;
  const { t } = useTranslation();

  const { usersStore, inviteStore } = useStores();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedUser, setSelectedUser] = useState<UserDataAndOrganization | null>(null);

  const steps = useMemo(
    () => [
      { label: t('selectUser'), id: 'selectUser' },
      { label: t('selectOrganization'), id: 'selectOrganization' },
      { label: t('inviteUser'), id: 'inviteUser' },
    ],
    []
  );

  const onCloseHandler = useCallback(() => {
    onClose();
    setTimeout(() => {
      setActiveStep(0);
      setSelectedUser(null);
    }, 500);
  }, []);

  const onSelectUserHandler = (user: UserData) => {
    if (!user) return;
    setSelectedUser({ ...user, type: UserType.Admin, organization: user.organizationId ? organizationData : null });
    setActiveStep(1);
  };

  const onChangeUserTypeHandler = useCallback(
    (userType: UserType) => {
      if (!selectedUser || !userType) return;
      setSelectedUser({ ...selectedUser, type: userType as string });
    },
    [selectedUser]
  );

  const onInviteUserHandler = () => {
    if (!selectedUser || !selectedUser.type || !organizationData.id || selectedUser.email.isNullOrEmpty()) return;

    const invitation = {
      organizationId: organizationData.id,
      firstName: selectedUser.firstName,
      lastName: selectedUser.lastName,
      email: selectedUser.email,
      type: selectedUser.type as string,
    };

    inviteStore.sendInvite({ invitation }, () => {
      usersStore.getUsers(organizationData.id);
      onCloseHandler();
    });
  };

  const onAddUserDirectlyHandler = () => {
    if (!selectedUser || !organizationData.id) return;
    usersStore.addDirectly(selectedUser, organizationData.id);
    onCloseHandler();
  };

  return (
    <AppDialog
      classNames={`user-dialog child-align-items-start child-m-h-700px overflow-auto`}
      title={`${t('addOrInviteUser')}`}
      isOpened={isOpened}
      onClose={onCloseHandler}
    >
      <div className='flex-auto d-flex flex-column justify-content-between'>
        <AppSectionErrorBoundary>
          <div>
            <section className='container'>
              <Stepper alternativeLabel activeStep={activeStep}>
                {steps.map(({ label }) => {
                  const stepProps: { completed?: boolean } = {};

                  return (
                    <Step key={label} {...stepProps}>
                      <StepLabel StepIconComponent={StepIconComponent}>{label}</StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
            </section>
            <AppSpacer space='20px' />
            <section className='container'>
              {activeStep === 0 && <SelectUserStep selectedUser={selectedUser} onSelectUser={onSelectUserHandler} />}
              {activeStep === 1 && (
                <SelectOrganizationStep
                  selectedUser={selectedUser}
                  organization={organizationData}
                  onChangeUserType={onChangeUserTypeHandler}
                  onClickNext={() => setActiveStep((prevActiveStep) => prevActiveStep + 1)}
                />
              )}
              {activeStep === 2 && (
                <InviteUserStep
                  onInviteUser={onInviteUserHandler}
                  onAddDirectly={onAddUserDirectlyHandler}
                  isInvitingUser={inviteStore.isSendingInvite}
                  isAddingUser={usersStore.isGrantingAccess}
                  allowDirectAdd={(selectedUser?.id && allowedDirectAddDomains.some(domain => selectedUser?.email.toLowerCase().endsWith(domain))) || false}
                />
              )}
            </section>
            <AppSpacer space='20px' />
          </div>
          <footer className='d-flex align-items-center justify-content-between p-3 bg-white position-sticky border-top bottom-0 left-0 w-100 z-index-9'>
            <div>
              <AppButton variant='secondary' onClick={onCloseHandler} data-test='user-dialog-cancel-button'>
                {t('cancel')}
              </AppButton>
            </div>
            <div className='d-flex align-items-center gap-2'>
              <AppButton
                variant='secondary'
                disabled={activeStep === 0}
                onClick={() => {
                  if (activeStep === 0) return;
                  setActiveStep((prevActiveStep) => prevActiveStep + -1);
                }}
                data-test='user-dialog-cancel-button'
              >
                {t('back')}
              </AppButton>
            </div>
          </footer>
        </AppSectionErrorBoundary>
      </div>
    </AppDialog>
  );
});

const StepIconComponent = (props: StepIconProps) => {
  const { active, completed, className } = props;

  return (
    <div className={`add-user-dialog-stepper ${active ? 'active' : ''} ${completed ? 'completed' : ''} ${className}`}>
      {completed ? <Check className='completedIcon' /> : <div className='circle' />}
    </div>
  );
};
